import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import Button from "@mui/material/Button/Button";
import TextField from "@mui/material/TextField/TextField";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import { ColDef, IGetRowsParams } from "ag-grid-community";
import { ILanguagesDto } from "../../../@types/curriculum.type";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod/dist/zod.js";
import { z } from "zod";
import { ButtonDeleted } from "../../../components/ButtonDeleted";
import { ButtonEdited } from "../../../components/ButtonEdited";
import Autocomplete from "@mui/material/Autocomplete/Autocomplete";
import Select from "@mui/material/Select/Select";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import FormControl from "@mui/material/FormControl/FormControl";
import InputLabel from "@mui/material/InputLabel/InputLabel";
import { getAllLanguageReference } from "../../../services/api/Curriculum/Language/languageService";

const languageSchema = z.object({
  id: z.string().optional(),
  language: z
    .object({
      id: z.string(),
      description: z.string(),
    }),
  levelId: z
    .string()
});

export type languageData = z.infer<typeof languageSchema>;

interface LanguageProps {
  setValue: React.Dispatch<React.SetStateAction<ILanguagesDto[]>>;
  languageValue: ILanguagesDto[];
  isEdit: boolean;
}

export const Language: React.FC<LanguageProps> = ({
  setValue,
  languageValue,
  isEdit,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [optionsSkills, setOptionsSkills] = useState<ILanguagesDto[]>([]);


  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<languageData>({
    resolver: zodResolver(languageSchema),
    defaultValues: {
      language: {},
      levelId: "",
    },
  });

  const onDelete = async (index: string | undefined) => {
    if (index) {
      const i = Number(index);
      if (!isNaN(i)) {
        setValue((prev) => {
          const updated = [...prev];
          updated.splice(i, 1);
          return updated;
        });
      }
    }
  };

  const onDeleteRef = useRef<(index: string | undefined) => Promise<void>>(
    async () => {}
  );
  useEffect(() => {
    onDeleteRef.current = onDelete;
  });

  async function fetchLanguages(){
    const response = await getAllLanguageReference();
    setOptionsSkills(response);
  }

  useEffect(() => {
    try {
      fetchLanguages();
    } catch (error) {
      console.log(error)
    }
  }, []);

  function getLevel(levelId: string){
    switch (levelId) {
      case "228d0a4f-46ae-40dc-8117-973452c15f4e":
        return "Básico"
      case "1b608e44-b8aa-4a67-a8a3-055872cb1a16":
        return "Básico a Intermediário"
      case "ee5d3ca4-5a18-450d-9a70-2b806297b4b0":
        return"Intermediário"
      case "c627b9c4-b066-442a-89fe-e2be3effeeff":
        return "Avançado"
      case "e2ea8ad9-5ca0-4221-bd9e-7c074149bc70":
        return "Fluente ou Nativo"
      default:
        return "Básico"
    }
  }

  const columnsDefault: ColDef[] = [
    {
      field: "id",
      headerName: "ID",
      hide: true,
    },
    {
      field: "description",
      headerName: "Idioma",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "levelId",
      headerName: "Proeficiência",
      flex: 1,
      maxWidth: 200,
      cellRenderer: (params:CustomCellRendererProps) => {
        return getLevel(params.value)
      }
    },
    {
      field: "action",
      headerName: "Ação",
      flex: 0.5,
      minWidth: 70,
      filter: false,
      sortable: false,
      hide: !isEdit,
      cellRenderer: (params: CustomCellRendererProps) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: ".5rem",
            }}
          >
            {params && params.data && params.data.id && (
              <ButtonEdited
                handleEditeItem={() => editCertification(params.data.id)}
              />
            )}
            <ButtonDeleted
              handleDeleteItem={() => onDeleteRef.current(params.node.id)}
            />
          </div>
        );
      },
    },
  ];
  const [columns, setColumns] = useState(columnsDefault);

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      try {
        const response = languageValue;

        if (response) params.successCallback(response, response.length);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      }
    },
  });

  const addOrEditLanguage = async (data: any) => {
    if (data) {
      const language: ILanguagesDto = {
        id: data.language.id || null,
        description: data.language.description || null,
        levelId: data.levelId || null
      };
 
      if (!languageValue.some((x) => x.id === language.id)) {
        setValue((prev) => [...prev, language]);
      } 
      else {
        if (languageValue) {
          setValue((prev) => [
            ...prev.map((x) => (x.id == language.id ? language : x)),
          ]);
        }
      }

      reset({
        id: undefined,
        language: {},
        levelId: "",
      });
    }
  };

  function editCertification(id: string) {
    const language = languageValue.find((x) => x.id == id);
    if (language) {
      reset({
        language: language,
        levelId: language.levelId,
      });
    }
  }

  useEffect(() => {
    setColumns(columnsDefault);
  }, [isEdit]);

  return (
    <div className={styles.language}>
      {isEdit && (
        <form
          className={styles.formProject}
          onSubmit={handleSubmit(addOrEditLanguage)}
        >
          <div className={styles.fields}>
            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={optionsSkills}
                  getOptionLabel={(option) => option.description || ""}
                  value={field.value || {}}
                  onChange={(_, newValue) => field.onChange(newValue ? newValue : "")}
                  noOptionsText="Sem opções"
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={option.id}>
                        {option.description}
                      </li>
                    );
                  }}
                  getOptionDisabled={(option) => languageValue.some((x) => x.id === option.id) }
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Selecione suas Competências"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                    />
                  )}
                />
              )}
            />
            <FormControl sx={{width: "200px"}}>
              <InputLabel shrink id="type-field-label">
                Proeficiência
              </InputLabel>
              <Controller
                name="levelId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Proeficiência"
                    onChange={field.onChange}
                    value={field.value}
                    fullWidth
                    displayEmpty
                    error={!!errors.levelId}
                  >
                    <MenuItem value="228d0a4f-46ae-40dc-8117-973452c15f4e">Básico</MenuItem>
                    <MenuItem value="1b608e44-b8aa-4a67-a8a3-055872cb1a16">Básico a Intermediario</MenuItem>
                    <MenuItem value="ee5d3ca4-5a18-450d-9a70-2b806297b4b0">Intermediário</MenuItem>
                    <MenuItem value="c627b9c4-b066-442a-89fe-e2be3effeeff">Avançado</MenuItem>
                    <MenuItem value="e2ea8ad9-5ca0-4221-bd9e-7c074149bc70">Fluente ou Nativo</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              className={styles.btnsSave}
            >
              +
            </Button>
          </div>
        </form>
      )}
      <div className={`${styles.gridContainer}`}>
        <AgGridReact
          ref={gridRef}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          columnDefs={columns}
          overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
          defaultColDef={{
            sortable: false,
            editable: false,
            filter: false,
            floatingFilter: false,
          }}
          suppressCellFocus={true}
          rowStyle={{ cursor: "pointer" }}
          rowModelType="infinite"
          datasource={getDataSource()}
          animateRows={true}
          infiniteInitialRowCount={10}
        />
      </div>
    </div>
  );
};
